import { Link } from 'react-router-dom';
import { Zap, Droplet, Waves, ArrowRight, MoveRight, Ruler } from 'lucide-react';
import SpaceTypeIllustration from '../ui/SpaceTypeIllustration';
import { SPACE_TYPE_LABELS } from '../../lib/fit';
import { groupSpaces } from '../../lib/spaceGroups';
import { formatCents, formatFeet } from '../../lib/format';

/** Icons for the feature keys lib/spaceGroups.js returns. */
const FEATURE_ICONS = {
  is_pull_through: { label: 'Pull-through', Icon: MoveRight },
  has_power: { label: 'Power', Icon: Zap },
  has_water: { label: 'Water', Icon: Droplet },
  has_sewer: { label: 'Sewer', Icon: Waves },
};

/**
 * Every bookable space, grouped by what it actually is.
 *
 * Individual spaces are not listed. A yard with twenty-six free spaces has
 * perhaps ten distinct shapes, and a visitor choosing between "A-03" and "A-04"
 * at the same size and price is choosing between two identical things — the
 * decision they are really making is size, cover and price. Grouping also turns
 * inventory depth into the one number that changes behaviour: how many are left.
 *
 * The count is the honest one — spaces currently available, not total built.
 */
export default function VehicleStorageSection({ units, slug }) {
  const groups = groupSpaces(units);
  if (groups.length === 0) return null;

  const total = groups.reduce((n, g) => n + g.count, 0);

  return (
    <section className="border-t border-slate-200 bg-white">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <h2 className="text-2xl font-extrabold tracking-tight text-slate-900">Vehicle storage</h2>
        <p className="mt-2 text-slate-600">
          {total} space{total === 1 ? '' : 's'} available right now, in {groups.length}{' '}
          size{groups.length === 1 ? '' : 's'}. Every one is measured — the dimensions below are
          the space, not a category name.
        </p>

        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map((g) => (
            <li key={g.key}
                className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition hover:border-brand-400 hover:shadow-card">
              <div className="relative bg-slate-50 px-4 pt-4">
                <div className="h-24 w-full text-brand-600">
                  <SpaceTypeIllustration spaceType={g.space_type} lengthFt={g.length_ft}
                                         clearanceFt={g.height_clearance_ft} className="h-full w-full" />
                </div>
                <span className={`absolute right-3 top-3 rounded-full px-2.5 py-1 text-[12px] font-bold ${
                  g.count <= 2 ? 'bg-amber-100 text-amber-900' : 'bg-emerald-100 text-emerald-800'}`}>
                  {g.count <= 3 ? `Only ${g.count} left` : `${g.count} available`}
                </span>
              </div>

              <div className="flex flex-1 flex-col p-4">
                <p className="text-[12px] font-bold uppercase tracking-wide text-brand-700">
                  {SPACE_TYPE_LABELS[g.space_type] ?? g.space_type}
                </p>
                <p className="mt-1 text-xl font-extrabold text-slate-900">
                  {formatFeet(g.width_ft)} × {formatFeet(g.length_ft)}
                </p>
                {g.height_clearance_ft != null && (
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
                    <Ruler className="h-3.5 w-3.5 text-brand-500" aria-hidden="true" />
                    {formatFeet(g.height_clearance_ft)} clearance
                  </p>
                )}

                {g.featureKeys.length > 0 && (
                  <ul className="mt-3 flex flex-wrap gap-1.5">
                    {g.featureKeys.map((k) => FEATURE_ICONS[k]).filter(Boolean).map(({ label, Icon }) => (
                      <li key={label}
                          className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[12px] font-semibold text-slate-700">
                        <Icon className="h-3 w-3 text-brand-600" aria-hidden="true" /> {label}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-auto flex items-end justify-between gap-3 pt-4">
                  <p>
                    <span className="text-xl font-extrabold text-slate-900">{formatCents(g.min_rate_cents)}</span>
                    <span className="text-xs text-slate-500">/month</span>
                  </p>
                  <Link to={`/facilities/${slug}`}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-xs font-bold text-white hover:bg-brand-700">
                    Reserve <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </li>
          ))}
        </ul>

        <p className="mt-6 text-xs text-slate-500">
          Prices exclude tax and one-time move-in fees, which are shown in full before you commit.
          Enter your rig on the search page to see which of these fit it.
        </p>
      </div>
    </section>
  );
}
